/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { matchPath } from 'react-router-dom'

import type { ElasticsearchCluster, StackDeployment, ApmCluster } from '@modules/ui-types'
import { LDResourceContextUpdater } from '@modules/app/middleware/LaunchDarklyMiddleware'

import DeploymentHeader from '../DeploymentHeader'
import DeploymentHealthStatus from '../DeploymentHealthStatus'
import IlmMigrationSuccess from '../../IlmMigration/IlmMigrationSuccess'
import { shouldShowProblemInOverviewPage } from '../../../lib/healthProblems'
import {
  deploymentUrl,
  deploymentActivityUrl,
  deploymentHealthStatusUrl,
  deploymentGettingStartedUrl,
} from '../../../lib/urlBuilder'

import type { Location } from 'react-router-dom'
import type { FunctionComponent } from 'react'

type Props = {
  deployment: ElasticsearchCluster
  stackDeployment: StackDeployment | null
  apm?: ApmCluster | null
  location: Location<unknown>
  shouldShowRegion: boolean
}

const DeploymentWrapper: FunctionComponent<Props> = ({
  deployment,
  stackDeployment,
  location,
  shouldShowRegion,
  children,
}) => {
  const { id } = stackDeployment!
  const onOverviewPage = isOverviewPage(location)
  const onActivityPage = isActivityPage(location)
  const onHealthPage = isHealthPage(location)
  const isGettingStartedPage = onGettingStartedPage({ location, id })

  return (
    <section>
      {!isGettingStartedPage && (
        <Fragment>
          <LDResourceContextUpdater
            resource={{
              key: deployment.id,
              type: 'deployment',
              cloudProvider: deployment.cloudId ?? '',
              region: deployment.regionId,
              solutionType: stackDeployment?.settings?.solution_type ?? '',
            }}
          />
          <DeploymentHeader
            stackDeployment={stackDeployment}
            deployment={deployment}
            shouldShowRegion={shouldShowRegion}
            isOverviewPage={onOverviewPage}
            isHealthPage={onHealthPage}
          />

          {onOverviewPage && (
            <DeploymentHealthStatus
              stackDeployment={stackDeployment}
              hideActivityBits={onActivityPage}
              filterProblems={shouldShowProblemInOverviewPage}
            />
          )}
        </Fragment>
      )}

      <IlmMigrationSuccess stackDeployment={stackDeployment} />

      <div className='cluster-content'>{children}</div>
    </section>
  )
}

function isOverviewPage({ pathname }: Location): boolean {
  return Boolean(matchPath(deploymentUrl(':deploymentId'), pathname))
}

function isHealthPage({ pathname }: Location): boolean {
  return Boolean(matchPath(deploymentHealthStatusUrl(':deploymentId'), pathname))
}

export function isActivityPage({ pathname }: Location<unknown>): boolean {
  return Boolean(matchPath(deploymentActivityUrl(), pathname))
}

function onGettingStartedPage({ location, id }: { location: Location; id: string }): boolean {
  const { pathname } = location
  return Boolean(matchPath(deploymentGettingStartedUrl(id), pathname))
}

export default DeploymentWrapper
